/**
 * Dashboard version selector — controls whether Home renders the legacy
 * line-graph dashboard or the new Penpot-matched dashboard.
 *
 * Behavior:
 *   - Prod: default = old. Only Millard Sprinkler (company 1) sees the toggle
 *     and can opt into the new view. Selection persists in localStorage.
 *   - Anywhere else (uat / qa / local / dev): default = new, toggle visible
 *     to everyone so the toggle UI itself can be exercised before prod.
 *
 * Once the new dashboard is rolled out to everyone in prod, delete this file
 * and revert Home to `return <Dashboard />`.
 */

import { useCallback, useState } from "react";

const MILLARD_SPRINKLER_PROD_COMPANY_ID = 1;
const STORAGE_KEY = "eserv:dashboard-version";

export type DashboardVersion = "old" | "new";

function isProdEnv(): boolean {
  const url = process.env.REACT_APP_SERVER_URL || "";
  // Match api.eserv.com exactly — excludes api-uat / api-qa / api-dev.
  return /^https?:\/\/api\.eserv\.com(?:[/:]|$)/.test(url);
}

export function useDashboardVersion(companyId: number) {
  const prod = isProdEnv();
  const showToggle = prod
    ? companyId === MILLARD_SPRINKLER_PROD_COMPANY_ID
    : true;
  const defaultVersion: DashboardVersion = prod ? "old" : "new";

  const [version, setVersionState] = useState<DashboardVersion>(() => {
    if (!showToggle) return defaultVersion;
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      return stored === "new" || stored === "old" ? stored : defaultVersion;
    } catch {
      return defaultVersion;
    }
  });

  const setVersion = useCallback((next: DashboardVersion) => {
    setVersionState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* localStorage unavailable — keep in-memory only */
    }
  }, []);

  return { version, setVersion, showToggle };
}
